import { useState } from 'react';
import {
  useParams, useNavigate 
} from 'react-router-dom';
import {
  ArrowLeft, FileText, Truck, Package, CheckCircle, Ship, User, AlertCircle 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  usePurchaseOrders, useUpdatePOStatus 
} from '@/hooks/use-purchase-orders';
import { useDiscrepanciesByPO } from '@/hooks/use-discrepancies';
import {
  PO_STATUS_LABELS , DISCREPANCY_TYPE_LABELS 
} from '@/types/orders';
import { ResolutionRoom } from '@/features/orders';
import type { Discrepancy } from '@/types/orders';

export function OrderDetailsPage() {
  const { poId } = useParams<{ poId: string }>();
  const navigate = useNavigate();
  const [selectedDiscrepancy, setSelectedDiscrepancy] = useState<Discrepancy | null>(null);

  const {
    data, isLoading 
  } = usePurchaseOrders();
  const { data: discrepanciesData } = useDiscrepanciesByPO(poId);
  const updateStatus = useUpdatePOStatus();

  const order = data?.data.find((o) => o.poId === poId);
  const discrepancies = discrepanciesData?.data ?? [];
  const pendingCount = discrepancies.filter((d) => d.status === 'pending_supplier_response').length;

  const handleAcknowledge = () => {
    if (poId) {
      updateStatus.mutate({
        poId,
        action: 'acknowledge' 
      });
    }
  };

  const handleMarkShipped = () => {
    if (poId) {
      updateStatus.mutate({
        poId,
        action: 'ship' 
      });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 animate-pulse bg-slate-100 rounded" />
        <Card className="h-96 animate-pulse bg-slate-100 border-slate-200" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/orders/purchase-orders')} className="text-slate-600">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Orders
        </Button>
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-lg font-medium text-slate-900">Order not found</p>
            <p className="text-slate-500">This purchase order may have been removed.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: order.currency 
    }).format(amount);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  const canAcknowledge = order.status === 'sent';
  const canMarkShipped = order.status === 'acknowledged';
  const isDelivered = order.status === 'delivered';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/orders/purchase-orders')} className="text-slate-600">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-slate-900">{order.poNumber}</h1>
              <StatusBadge status={order.status} />
              {pendingCount > 0 && (
                <span className="flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 px-2.5 py-1 text-sm font-medium">
                  <AlertCircle className="h-3.5 w-3.5" />
                  {pendingCount} action{pendingCount === 1 ? '' : 's'} needed
                </span>
              )}
            </div>
            <p className="text-slate-500">{order.deliverySiteName}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {canAcknowledge && (
            <Button onClick={handleAcknowledge} disabled={updateStatus.isPending} className="bg-slate-900 hover:bg-slate-800">
              <CheckCircle className="h-4 w-4 mr-2" />
              Acknowledge Order
            </Button>
          )}
          {canMarkShipped && (
            <Button onClick={handleMarkShipped} disabled={updateStatus.isPending} className="bg-slate-900 hover:bg-slate-800">
              <Ship className="h-4 w-4 mr-2" />
              Mark as Shipped
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          {/* Pending Actions Section */}
          {isDelivered && discrepancies.length > 0 && (
            <Card className={`border-slate-200 ${pendingCount > 0 ? 'ring-1 ring-amber-200' : ''}`}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-medium text-slate-900">
                  <AlertCircle className={`h-5 w-5 ${pendingCount > 0 ? 'text-amber-600' : 'text-slate-400'}`} />
                  Delivery Issues
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedDiscrepancy ? (
                  <div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedDiscrepancy(null)}
                      className="mb-3 text-slate-600"
                    >
                      <ArrowLeft className="h-4 w-4 mr-1" />
                      Back to list
                    </Button>
                    <ResolutionRoom discrepancy={selectedDiscrepancy} compact />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {discrepancies.map((discrepancy) => (
                      <DiscrepancyRow
                        key={discrepancy.discrepancyId}
                        discrepancy={discrepancy}
                        onSelect={() => setSelectedDiscrepancy(discrepancy)}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Order Items */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base font-medium text-slate-900">
                <Package className="h-5 w-5 text-slate-400" />
                Order Items
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100 text-sm text-slate-500">
                      <th className="text-left py-3 font-medium">Item</th>
                      <th className="text-left py-3 font-medium">SKU</th>
                      <th className="text-right py-3 font-medium">Qty</th>
                      <th className="text-right py-3 font-medium">Unit Price</th>
                      <th className="text-right py-3 font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.lines.map((line) => (
                      <tr key={line.lineId} className="border-b border-slate-50 last:border-0">
                        <td className="py-3">
                          <p className="font-medium text-slate-900">{line.itemName}</p>
                        </td>
                        <td className="py-3 text-sm text-slate-500">{line.sku}</td>
                        <td className="py-3 text-right text-slate-700">
                          {line.quantity} {line.unit}
                        </td>
                        <td className="py-3 text-right text-slate-700">{formatCurrency(line.unitPrice)}</td>
                        <td className="py-3 text-right font-medium text-slate-900">{formatCurrency(line.totalPrice)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-slate-100">
                      <td colSpan={4} className="py-3 text-right font-medium text-slate-700">Subtotal</td>
                      <td className="py-3 text-right font-medium text-slate-900">{formatCurrency(order.subtotal)}</td>
                    </tr>
                    {order.tax > 0 && (
                      <tr>
                        <td colSpan={4} className="py-2 text-right text-slate-500">Tax</td>
                        <td className="py-2 text-right text-slate-700">{formatCurrency(order.tax)}</td>
                      </tr>
                    )}
                    <tr className="text-lg">
                      <td colSpan={4} className="py-3 text-right font-semibold text-slate-900">Total</td>
                      <td className="py-3 text-right font-semibold text-slate-900">{formatCurrency(order.total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>

          {order.notes && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-base font-medium text-slate-900">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">{order.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base font-medium text-slate-900">
                <Truck className="h-5 w-5 text-slate-400" />
                Delivery Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <InfoRow label="Delivery Zone" value={order.deliveryZone} />
              <InfoRow label="Delivery Site" value={order.deliverySiteName} />
              <InfoRow label="Expected Delivery" value={formatDate(order.expectedDeliveryDate)} />
              {order.actualDeliveryDate && (
                <InfoRow label="Actual Delivery" value={formatDate(order.actualDeliveryDate)} />
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base font-medium text-slate-900">
                <User className="h-5 w-5 text-slate-400" />
                Order Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <InfoRow label="Created By" value={order.createdByName} />
              {order.approvedByName && <InfoRow label="Approved By" value={order.approvedByName} />}
              <InfoRow label="Created" value={formatDate(order.createdAt)} />
              <InfoRow label="Last Updated" value={formatDate(order.updatedAt)} />
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base font-medium text-slate-900">
                <FileText className="h-5 w-5 text-slate-400" />
                Order Timeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <TimelineItem label="Order Created" date={order.createdAt} completed />
                <TimelineItem label="Approved" date={order.approvedBy ? order.updatedAt : undefined} completed={!!order.approvedBy} />
                <TimelineItem label="Sent to Supplier" completed={['sent', 'acknowledged', 'shipped', 'delivered'].includes(order.status)} />
                <TimelineItem label="Acknowledged" completed={['acknowledged', 'shipped', 'delivered'].includes(order.status)} />
                <TimelineItem label="Shipped" completed={['shipped', 'delivered'].includes(order.status)} />
                <TimelineItem label="Delivered" date={order.actualDeliveryDate} completed={order.status === 'delivered'} />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { readonly status: string }) {
  const isDelivered = status === 'delivered';
  return (
    <span className={`rounded-full px-3 py-1 text-sm font-medium ${
      isDelivered ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-700'
    }`}>
      {PO_STATUS_LABELS[status as keyof typeof PO_STATUS_LABELS]}
    </span>
  );
}

function InfoRow({
  label, value 
}: {
  readonly label: string;
  readonly value: string 
}) {
  return (
    <div>
      <p className="text-sm text-slate-500">{label}</p>
      <p className="font-medium text-slate-900">{value}</p>
    </div>
  );
}

interface DiscrepancyRowProps {
  readonly discrepancy: Discrepancy;
  readonly onSelect: () => void;
}

function DiscrepancyRow({
  discrepancy, onSelect 
}: DiscrepancyRowProps) {
  const needsResponse = discrepancy.status === 'pending_supplier_response';
  const isResolved = discrepancy.status === 'resolved';

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left p-3 rounded-md border transition-colors ${
        needsResponse
          ? 'border-amber-200 bg-amber-50/50 hover:bg-amber-50'
          : 'border-slate-100 bg-slate-50/50 hover:bg-slate-50'
      }`}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-900 text-sm">
              {DISCREPANCY_TYPE_LABELS[discrepancy.type]}
            </span>
            {needsResponse && (
              <span className="text-xs text-amber-700 font-medium">Action needed</span>
            )}
            {isResolved && (
              <span className="flex items-center gap-1 text-xs text-emerald-600">
                <CheckCircle className="h-3 w-3" />
                Resolved
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            {discrepancy.items.length} item{discrepancy.items.length === 1 ? '' : 's'} • 
            Reported by {discrepancy.reportedByName}
          </p>
        </div>
        <span className="text-slate-400 text-sm">View →</span>
      </div>
    </button>
  );
}

interface TimelineItemProps {
  readonly label: string;
  readonly date?: string;
  readonly completed: boolean;
}

function TimelineItem({
  label, date, completed 
}: TimelineItemProps) {
  return (
    <div className="flex items-center gap-3">
      <div className={`h-2.5 w-2.5 rounded-full ${completed ? 'bg-emerald-500' : 'bg-slate-200'}`} />
      <div className="flex-1">
        <p className={`text-sm ${completed ? 'font-medium text-slate-900' : 'text-slate-400'}`}>{label}</p>
        {date && (
          <p className="text-xs text-slate-400">
            {new Date(date).toLocaleDateString()}
          </p>
        )}
      </div>
    </div>
  );
}
